"use client";

import { useMemo, useState } from "react";
import { Notice } from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  PlainLanguagePanel,
  StatusList,
} from "@/components/tools/research-workflow/check-parts";
import { ConsentFields } from "@/components/tools/research-workflow/consent-fields";
import {
  DocumentActions,
  DocumentPreview,
} from "@/components/tools/research-workflow/document-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  buildConsentForm,
  type ConsentInput,
  checkConsentElements,
  consentProse,
  EMPTY_CONSENT_INPUT,
  mentionsAnonymity,
} from "@/lib/tool-kit/research-workflow/consent";
import { isoDateToLocal } from "@/lib/tool-kit/research-workflow/dates";
import {
  docToHtml,
  docToText,
} from "@/lib/tool-kit/research-workflow/document";

/** English strings. A translated content file overrides these through `ui`. */
export const INFORMED_CONSENT_FORM_GENERATOR_STRINGS = {
  studyPanel: "The study and who to contact",
  proceduresPanel: "What participants will do",
  risksPanel: "Risks, benefits and alternatives",
  privacyPanel: "Privacy and future use of data",
  extrasPanel: "Payment, withdrawal and optional elements",
  signingPanel: "How consent is recorded",
  titleLabel: "Study title",
  titlePlaceholder: "e.g. Sleep and Study Habits of First-Year Students",
  researcherLabel: "Researcher's name",
  researcherPlaceholder: "e.g. Jordan Lee",
  roleLabel: "Role and department (optional)",
  rolePlaceholder: "e.g. master's student, Department of Psychology",
  institutionLabel: "Institution",
  institutionPlaceholder: "e.g. Riverside University",
  advisorLabel: "Faculty advisor, if any — name and email",
  advisorPlaceholder: "e.g. Dr. Ana Ruiz, a.ruiz@riverside.example.edu",
  contactLabel: "Researcher's email or phone",
  contactPlaceholder: "e.g. j.lee@riverside.example.edu",
  irbLabel: "IRB or research ethics office",
  irbPlaceholder: "e.g. the Riverside University Institutional Review Board",
  irbContactLabel: "IRB email or phone",
  irbContactPlaceholder: "e.g. irb@riverside.example.edu",
  protocolLabel: "IRB protocol number (optional)",
  versionLabel: "Version date (optional)",
  firstSentenceHint:
    "Put the main point in the first sentence of each box — the key-information summary at the top of the form quotes it.",
  purposeLabel: "Purpose — why the study is being done",
  purposePlaceholder:
    "e.g. We want to learn how sleep schedules relate to study habits during the first year of university.",
  proceduresLabel: "Procedures — what participants will do",
  proceduresPlaceholder:
    "e.g. You will answer an online questionnaire about your sleep and study routines.",
  durationLabel: "Time commitment",
  durationPlaceholder: "e.g. about 20 minutes, in one online session",
  experimentalLabel: "Experimental procedures, if any — one per line",
  experimentalPlaceholder: "Leave blank if no procedure is experimental.",
  participantsLabel: "Approximate number of participants (optional)",
  riskLevelLabel: "Risk level, as your IRB is likely to judge it",
  riskMinimal: "Minimal risk",
  riskGreater: "More than minimal risk",
  risksLabel: "Risks or discomforts",
  risksPlaceholder:
    "e.g. Some questions about sleep may feel personal. You can skip any question.",
  injuryLabel: "Compensation and treatment if an injury occurs",
  injuryPlaceholder:
    "Say whether compensation or medical treatment is available if someone is injured, what it consists of, or where to find out more.",
  benefitsLabel: "Benefits to participants or to others",
  benefitsPlaceholder:
    "e.g. You will not benefit directly. The results may help universities support students' sleep.",
  alternativesLabel: "Alternatives (optional)",
  alternativesPlaceholder:
    "Leave blank for “Your alternative is not to take part”, or describe one — such as another assignment for the same course credit.",
  confidentialityLabel:
    "Confidentiality — how identifying records are protected",
  confidentialityPlaceholder:
    "e.g. Your answers will be stored on a password-protected university drive that only the research team can open. We will report only combined results.",
  dataKindLabel: "What will you collect?",
  dataInformationOption: "Information only — answers, recordings, records",
  dataSpecimensOption: "Information and biospecimens — blood, saliva, tissue",
  futureUseLabel: "Future research use, §46.116(b)(9)",
  futureMayOption: "Identifiers removed, then may be used or shared",
  futureWillNotOption: "Will not be used or shared for future research",
  futureNoneOption: "Nothing identifiable is collected",
  paymentLabel: "Payment or course credit (optional)",
  paymentPlaceholder:
    "e.g. You will receive a $10 gift card, even if you stop early.",
  costsLabel: "Costs to participants (optional)",
  costsPlaceholder: "e.g. Taking part will not cost you anything.",
  withdrawalLabel: "What happens to data if someone stops (optional)",
  withdrawalPlaceholder:
    "e.g. If you stop, you can ask us to delete the answers you have already given.",
  terminationLabel: "When the researcher may end someone's participation",
  terminationPlaceholder: "e.g. if you no longer meet the study criteria",
  resultsLabel: "Sharing individual results (optional)",
  resultsPlaceholder:
    "e.g. We will not share individual results, but you can ask for a summary of the findings.",
  unforeseeableCheck: "Some risks may not be known yet — (c)(1)",
  pregnancyCheck: "Unknown risks to an embryo or fetus — (c)(1)",
  newFindingsCheck:
    "Participants will hear of significant new findings — (c)(5)",
  studentsCheck:
    "Participants are your students or employees — say grades and jobs are unaffected",
  profitLabel: "Commercial profit from samples, (c)(7)",
  genomeLabel: "Whole genome sequencing, (c)(9)",
  omitOption: "Do not include",
  profitWillOption: "May be used for profit; participants share",
  profitWillNotOption: "May be used for profit; participants do not share",
  genomeWillOption: "Will be performed",
  genomeMightOption: "Might be performed",
  recordingLabel: "Recording",
  recordingNone: "No recording",
  recordingAudioOption: "Audio",
  recordingVideoOption: "Video",
  recordingBothOption: "Audio and video",
  documentationLabel: "Documentation",
  documentationSigned: "Signed form",
  documentationImplied: "No signature (IRB waiver)",
  impliedNote:
    "Use this only if your IRB has waived signed consent under 45 CFR 46.117(c) — common for anonymous online surveys.",
  ageLabel: "Minimum age",
  impliedActionLabel: "How people agree",
  impliedActionPlaceholder: "e.g. select “I agree” and continue to the survey",
  larCheck: "Add lines for a legally authorized representative",
  witnessCheck: "Add lines for a witness",
  irbReminder:
    "A draft, not an approved form. If your institution has an IRB consent template, paste these sections into it and keep its required wording; every consent form must be approved by your IRB before you use it.",
  previewHeading: "Your consent form",
  previewLabel: "Consent form preview",
  checklistHeading: "Required elements",
  missingCount:
    "one:{count} element still needs your words.|other:{count} elements still need your words.",
  allCovered:
    "Every required element is covered. Now read the form as a participant would.",
  statusCovered: "Covered",
  statusMissing: "Needs your words",
  statusStandard: "Standard wording",
  statusNa: "Not applicable",
  elements: {
    keyInfo: "Key information first — §46.116(a)(5)(i)",
    b1: "Research, purpose, duration, procedures — (b)(1)",
    b2: "Foreseeable risks or discomforts — (b)(2)",
    b3: "Expected benefits — (b)(3)",
    b4: "Alternatives — (b)(4)",
    b5: "Confidentiality of records — (b)(5)",
    b6: "Compensation and treatment for injury — (b)(6)",
    b7: "Contacts for questions, rights and injury — (b)(7)",
    b8: "Voluntary participation, no penalty — (b)(8)",
    b9: "Future use of data or biospecimens — (b)(9)",
    documentation: "Signature or waived documentation — §46.117",
  },
  plainHeading: "Plain-language check",
  longCount:
    "one:{count} sentence runs past 25 words:|other:{count} sentences run past 25 words:",
  longItem: "one:“{start} …” — {count} word|other:“{start} …” — {count} words",
  jargonLead: "Plainer words for participants:",
  jargonItem: "{term} → {plain}",
  plainClear: "No long sentences or flagged jargon in what you have written.",
  anonymousWarning:
    "You wrote “anonymous”. That is only true if nobody — including you — can link an answer to a person through a name, an email address, an IP address or a signed form. If someone could, write “confidential” instead.",
  jargon: [
    "utilize = use",
    "utilise = use",
    "prior to = before",
    "subsequent to = after",
    "in order to = to",
    "in the event that = if",
    "commence = start",
    "terminate = end",
    "approximately = about",
    "sufficient = enough",
    "adverse = harmful",
    "efficacy = how well it works",
    "remuneration = payment",
    "pursuant to = under",
    "aforementioned = this, or name the thing",
    "hereby = (cut it)",
    "investigator = researcher",
    "protocol = study plan",
    "subjects = participants, or “you”",
    "randomized = assigned by chance",
    "randomised = assigned by chance",
    "placebo = explain it: a look-alike with no active ingredient",
    "minimal risk = no more risk than everyday life",
    "de-identified = with names and other identifiers removed",
    "aggregate = combined",
    "longitudinal = repeated over time",
  ],
  copyText: "Copy as text",
  copyFormatted: "Copy for Word or Google Docs",
  copied: "Copied!",
  downloadText: "Download .txt",
  docTitle: "Consent to Take Part in a Research Study",
  metaStudy: "Study title: {value}",
  metaResearcher: "Researcher: {value}",
  metaInstitution: "Institution: {value}",
  metaAdvisor: "Faculty advisor: {value}",
  metaProtocol: "IRB protocol number: {value}",
  metaVersion: "Version date: {value}",
  keyHeading: "Key information about this study",
  keyIntro:
    "You are being asked to take part in a research study. Taking part is voluntary — it is your choice. This section sums up the most important facts; more detail follows.",
  keyWhy: "Why we are doing this study: {text}",
  keyWhat: "What you will do: {text}",
  keyTime: "How long it will take: {text}",
  keyRisks: "Main risks: {text}",
  keyBenefits: "Possible benefits: {text}",
  keyAlternatives: "Your other choices: {text}",
  purposeHeading: "Why is this study being done?",
  purposeLead: "This is a research study.",
  proceduresHeading: "What will I be asked to do?",
  durationSentence: "Taking part will take {duration}.",
  experimentalLead:
    "These parts of the study are experimental — they are being tested and are not standard practice:",
  participantsSentence: "About {count} people will take part in this study.",
  risksHeading: "What are the risks or discomforts?",
  unforeseeableSentence:
    "Taking part may also involve risks that are not known yet.",
  pregnancySentence:
    "If you are or may become pregnant, taking part may involve risks to an embryo or fetus that are not known yet.",
  benefitsHeading: "What are the possible benefits?",
  alternativesHeading: "What other choices do I have?",
  alternativesDefault: "Your alternative is not to take part in this study.",
  confidentialityHeading: "How will my information be kept private?",
  futureHeading: "Will my information be used for future research?",
  futureMay:
    "We will remove information that could identify you, such as your name, from your {data}. After that, your {data} could be used for future research studies, or shared with other researchers for future research studies, without asking for your consent again.",
  futureWillNot:
    "Your {data} collected for this study will not be used or shared for future research studies, even if information that could identify you is removed.",
  dataInformation: "information",
  dataSpecimens: "information and samples",
  samplesHeading: "What will happen to my samples?",
  profitWill:
    "Your samples, even with identifiers removed, may be used for commercial profit. You will share in this profit.",
  profitWillNot:
    "Your samples, even with identifiers removed, may be used for commercial profit. You will not share in this profit.",
  genomeWill:
    "This research will include whole genome sequencing — reading the complete genetic code in your sample.",
  genomeMight:
    "This research might include whole genome sequencing — reading the complete genetic code in your sample.",
  paymentHeading: "Payment and costs",
  injuryHeading: "What happens if I am hurt by taking part?",
  stopHeading: "What if I do not want to take part, or want to stop?",
  voluntarySentence:
    "Taking part in this study is voluntary. You can decide not to take part, and you can stop at any time. If you decide not to take part, or you stop, there will be no penalty, and you will not lose any benefits you are otherwise entitled to.",
  studentsSentence:
    "Your decision will not affect your grades, your job or your standing at {institution}.",
  terminationLead:
    "The researcher may end your part in the study without your agreement in this situation:",
  findingsHeading: "Will I be told about new findings or my results?",
  newFindingsSentence:
    "We will tell you about any important new findings during the study that might affect whether you want to keep taking part.",
  recordingHeading: "Recording",
  recordingAudio:
    "We would like to audio-record the session. Please initial one choice.",
  recordingVideo:
    "We would like to video-record the session. Please initial one choice.",
  recordingBoth:
    "We would like to audio- and video-record the session. Please initial one choice.",
  recordingChoice: "I agree to be recorded.   Yes {short}   No {short}",
  contactHeading: "Who can I contact with questions?",
  contactStudy:
    "If you have questions about this study, contact {researcher} at {contact}.",
  contactAdvisor: "You can also contact the faculty advisor, {advisor}.",
  contactInjury:
    "If you think you have been harmed by taking part, contact {researcher} at {contact}.",
  contactRights:
    "If you have questions about your rights as a research participant, or want to talk to someone outside the research team, contact {irb} at {irbContact}.",
  consentHeading: "Statement of consent",
  consentStatement:
    "I have read this form, or had it read to me. I have had the chance to ask questions, and my questions have been answered. I am at least {age} years old, and I agree to take part in this study.",
  copySentence: "You will be given a copy of this form to keep.",
  fieldName: "Participant's name (printed): {line}",
  fieldSignature: "Participant's signature: {line}   Date: {short}",
  fieldObtainerName: "Name of person obtaining consent: {line}",
  fieldObtainerSignature:
    "Signature of person obtaining consent: {line}   Date: {short}",
  fieldLarName: "Name of legally authorized representative: {line}",
  fieldLarRelationship: "Relationship to the participant: {line}",
  fieldLarSignature:
    "Signature of legally authorized representative: {line}   Date: {short}",
  fieldWitnessName: "Name of witness: {line}",
  fieldWitnessSignature: "Signature of witness: {line}   Date: {short}",
  impliedHeading: "Agreeing to take part",
  impliedStatement:
    "If you are at least {age} years old, have read this information and agree to take part, {action}. If you do not want to take part, you do not need to do anything.",
  impliedActionDefault: "continue to the survey",
  keepCopySentence: "Please keep a copy of this information for your records.",
  missingTitle: "[study title]",
  missingResearcher: "[researcher's name]",
  missingInstitution: "[institution]",
  missingContact: "[email or phone]",
  missingIrb: "[IRB or ethics office]",
  missingIrbContact: "[IRB email or phone]",
  missingPurpose: "[Explain the purpose of the study in one or two sentences.]",
  missingProcedures: "[Describe what participants will do, step by step.]",
  missingDuration: "[time commitment]",
  missingRisks:
    "[Describe each foreseeable risk or discomfort, or say that none are expected beyond those of everyday life.]",
  missingBenefits:
    "[Describe any benefit to participants or to others. If there is no direct benefit, say so.]",
  missingConfidentiality:
    "[Explain where identifying records are kept, who can see them, and when identifiers are deleted.]",
  missingInjury:
    "[Explain whether compensation and medical treatment are available if an injury occurs, what they consist of, or where to find more information.]",
} as const;

export default function InformedConsentFormGeneratorWidget() {
  const strings = useToolUi(INFORMED_CONSENT_FORM_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<ConsentInput>(EMPTY_CONSENT_INPUT);
  const [count, setCount] = useState("");
  const [date, setDate] = useState("");

  const set = <K extends keyof ConsentInput>(key: K, value: ConsentInput[K]) =>
    setInput((current) => ({ ...current, [key]: value }));

  const participants = parseIntegerInput(count);
  const resolved = useMemo<ConsentInput>(
    () => ({
      ...input,
      participants:
        participants !== null && participants > 0 ? fmt(participants) : "",
      versionDate: isoDateToLocal(date, locale),
    }),
    [input, participants, date, locale, fmt]
  );
  const doc = useMemo(
    () => buildConsentForm(resolved, strings, locale),
    [resolved, strings, locale]
  );
  const checks = useMemo(() => checkConsentElements(resolved), [resolved]);
  const missing = checks.filter((check) => check.status === "missing").length;
  const text = useMemo(() => docToText(doc), [doc]);
  const html = useMemo(() => docToHtml(doc), [doc]);

  return (
    <div className="space-y-6">
      <Notice>{strings.irbReminder}</Notice>
      <ConsentFields
        count={count}
        date={date}
        input={input}
        onCount={setCount}
        onDate={setDate}
        set={set}
        strings={strings}
      />

      <section className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.checklistHeading}</h2>
        <p aria-live="polite" className="text-foreground text-sm">
          {missing > 0
            ? tPlural(strings.missingCount, locale, missing, {
                count: fmt(missing),
              })
            : strings.allCovered}
        </p>
        <StatusList
          items={checks.map((check) => ({
            id: check.element,
            label: strings.elements[check.element],
            status: check.status,
          }))}
          labels={{
            covered: strings.statusCovered,
            missing: strings.statusMissing,
            standard: strings.statusStandard,
            na: strings.statusNa,
          }}
        />
      </section>

      <PlainLanguagePanel
        extraWarning={
          mentionsAnonymity(input) ? strings.anonymousWarning : null
        }
        heading={strings.plainHeading}
        jargon={strings.jargon}
        labels={strings}
        locale={locale}
        text={consentProse(input)}
      />

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.previewHeading}</h2>
        <DocumentActions
          filename="informed-consent-form"
          html={html}
          labels={strings}
          text={text}
        />
        <DocumentPreview doc={doc} label={strings.previewLabel} />
      </section>
    </div>
  );
}
